/**
 * Size / name formatting shared by the reader, compose and settings (spec §10, §12).
 */

const MIB = 1048576;

/** Attachment sizes: `> 1 MiB → x.xMB`, else `ceil(KB)KB`. */
export function formatSize(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes)) return '';
  return bytes > MIB ? `${(bytes / MIB).toFixed(1)}MB` : `${Math.ceil(bytes / 1024)}KB`;
}

/** Quota figures: `≥ 1 GiB → x.x GB`, else `MB` (at least 1). */
export function formatQuota(bytes: number): string {
  return bytes >= 1024 ** 3
    ? (bytes / 1024 ** 3).toFixed(1) + ' GB'
    : Math.max(1, Math.round(bytes / 1024 ** 2)) + ' MB';
}

/** Quota state for the Settings progress bar (`warn` > 80 %, `full` > 95 %). */
export function quotaLevel(used: number, limit: number): 'ok' | 'warn' | 'full' {
  if (!limit) return 'ok';
  const pct = (used / limit) * 100;
  if (pct > 95) return 'full';
  if (pct > 80) return 'warn';
  return 'ok';
}

/** Dovecot reports quota in KiB; convert for display. */
export function quotaBytesFromKib(kib: number): number {
  return kib * 1024;
}

/** Avatar initial: first character of the name, else the address, upper-cased. */
export function initialOf(name: string | null | undefined, addr: string | null | undefined): string {
  const s = (name || addr || '').trim();
  return s ? s.charAt(0).toUpperCase() : '';
}

/**
 * Truncate a filename to `max` chars keeping a short extension (≤ 6 chars incl. the dot), e.g.
 * `"very-long-report-name.pdf"` → `"very-long-rep…pdf"` at 20 (legacy list chip rule).
 */
export function truncateAttachmentName(name: string, max = 20): string {
  if (name.length <= max) return name;
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 && name.length - dot <= 6 ? name.slice(dot) : '';
  return name.slice(0, Math.max(4, max - ext.length - 1)) + '…' + ext;
}

/** `Name <addr>` or just the address (used by the .eml preview meta line). */
export function formatPerson(p: { name?: string | null; address?: string | null }): string {
  const n = (p.name || '').trim();
  const a = (p.address || '').trim();
  return n && a ? `${n} <${a}>` : a || n;
}
